import { PhoneNumberDisplayRow, isCanadaNumber, isExtensionNumber, isUsNumber } from '@/utils/phoneNumberCapabilities';
import { countryCodeFromPhoneNumber, normalizeCountryCode } from '@/utils/phoneNumberBilling';

type NumberLike = PhoneNumberDisplayRow & {
  provider?: string | null;
  capabilities?: string[] | null;
  kind?: string | null;
};

function capabilitySet(capabilities?: string[] | null) {
  return new Set((capabilities ?? []).map((cap) => cap.toLowerCase()));
}

function providerOf(number: NumberLike | null | undefined) {
  return number?.provider?.toLowerCase() || '';
}

/** NetSapiens (and similar) extension inventory — Enable SMS + linked DID picker. */
export function isExtensionEndpoint(number: NumberLike | null | undefined) {
  if (!number) return false;
  if (providerOf(number) === 'netsapiens') return true;
  if (number.kind === 'extension' && providerOf(number) === 'netsapiens') return true;
  if (isExtensionNumber(number) && providerOf(number) === 'netsapiens') return true;
  return false;
}

/** SIP registration / trunk / SIP extension (not NetSapiens / Asterisk). */
export function isSipEndpoint(number: NumberLike | null | undefined) {
  if (!number || isExtensionEndpoint(number)) return false;
  if (providerOf(number) === 'asterisk') return false;

  const provider = providerOf(number);
  if (provider === 'sip_connectivity' || provider === 'ring_central') return true;
  if (number.kind === 'sip_registration' || number.kind === 'sip_trunk') return true;

  const type = String(number.connection_config?.type || '');
  return type === 'sip_registration' || type === 'sip_trunking';
}

/** SMS agent roles apply when the number has SMS / Hosted SMS / A2P, or is an extension with linked SMS. */
export function showSmsAgentSections(number: NumberLike | null | undefined) {
  if (!number) return false;
  if (isExtensionEndpoint(number)) return true;
  const caps = capabilitySet(number.capabilities);
  return caps.has('sms') || caps.has('hosted_sms') || caps.has('a2p');
}

/** Resolved ISO country for add-on gating (stored code, else parse from E.164 / SIP main line). */
export function resolvedCountryCode(number: NumberLike | null | undefined): string {
  const stored = normalizeCountryCode(number?.country_code);
  if (stored) return stored;

  const mainLine = String(number?.connection_config?.main_line_number || '').trim();
  if (mainLine) {
    const fromMain = normalizeCountryCode(countryCodeFromPhoneNumber(mainLine));
    if (fromMain) return fromMain;
  }

  return normalizeCountryCode(countryCodeFromPhoneNumber(number?.phone_number)) || '';
}

/**
 * Host SMS card.
 * SIP: CA or US. Non-SIP: US (or already hosted_sms). Not for NetSapiens / Asterisk.
 */
export function showHostSmsAddOn(number: NumberLike | null | undefined) {
  if (!number || isExtensionEndpoint(number)) return false;
  if (providerOf(number) === 'asterisk') return false;

  const caps = capabilitySet(number.capabilities);
  if (caps.has('hosted_sms')) return true;

  const code = resolvedCountryCode(number);
  if (isSipEndpoint(number)) {
    return code === 'CA' || code === 'US' || isCanadaNumber(number) || isUsNumber(number);
  }

  return code === 'US' || isUsNumber(number);
}

/**
 * A2P card.
 * SIP: US only. Non-SIP: US (or already a2p). Not for NetSapiens / Asterisk.
 */
export function showA2pAddOn(number: NumberLike | null | undefined) {
  if (!number || isExtensionEndpoint(number)) return false;
  if (providerOf(number) === 'asterisk') return false;

  const caps = capabilitySet(number.capabilities);
  if (caps.has('a2p')) return true;

  const code = resolvedCountryCode(number);
  if (isSipEndpoint(number)) {
    return code === 'US' || isUsNumber(number);
  }

  return code === 'US' || isUsNumber(number);
}

/** Either Host SMS or A2P add-on section should render. */
export function showAddOnSections(number: NumberLike | null | undefined) {
  return showHostSmsAddOn(number) || showA2pAddOn(number);
}

export function defaultCountryFromNumber(number: NumberLike | null | undefined): string {
  const code = number?.country_code?.toUpperCase().replace(/[^A-Z]/g, '') ?? '';
  if (code === 'CA' || code === 'CAN') return 'CA';
  if (code === 'IE' || code === 'IRL') return 'IE';
  if (code === 'GB' || code === 'UK') return 'GB';
  const fromPhone = resolvedCountryCode(number);
  if (fromPhone === 'CA' || fromPhone === 'US' || fromPhone === 'IE' || fromPhone === 'GB') return fromPhone;
  return 'US';
}
